import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Check,
  ChevronDown,
  Download,
  Globe,
  Link2,
  Loader2,
  Printer,
  QrCode,
  Settings2,
  Smartphone,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { GenieAvatar } from '@/components/GenieAvatar';
import { BaseSheet } from '@/components/common/BaseSheet';
import {
  useActivationPack,
  useActivationPacks,
  useRememberActivationPack,
  useUpdateActivationPack,
  useDeleteActivationPack,
} from '@/hooks/activation';
import { useAssetGenerator } from '@/hooks/activation';
import { ActivationFunnelService } from '@/services/analytics/ActivationFunnelService';
import { ActivationPackService } from '@/services/database/ActivationPackService';
import {
  ActivationBackOrigin,
  ChannelConfig,
  TouchpointConfig,
  AssetConfig,
  ChannelType,
  TouchpointType,
  DEFAULT_CHANNEL_CONFIG,
  DEFAULT_TOUCHPOINT_CONFIG,
  DEFAULT_ASSET_CONFIG,
} from '@/types/activation/activation-pack';
import {
  PackOverviewSection,
  PackChannelsSection,
  PackMaterialsSection,
  PackCustomizationSection,
  PackQuickLinksSection,
  PackPresentationSection,
  PackLookbookSection,
  PackPostersSection,
  PackAppOnboardingSection,
  PackActivateInAppSection,
  PackPromoteSection,
  PackAssetsSection,
  PackEmailSignatureSection,
  PackInviteSection,
  PackPrintCopySection,
} from './components/sections';
import { GenieLoader } from '@/components/ui/GenieLoader';
import { ActivateCustomersCard } from './components/ActivateCustomersCard';
import { PackActivateHero } from './components/PackActivateHero';
import { PackActivationTracker } from './components/PackActivationTracker';
import { PackBooklets } from './components/PackBooklets';
import {
  resolvePackPresetKit,
  resolvePackPrintKit,
} from '@/services/activation/print/packDocuments';
import { PackGenieSwitcher } from './components/PackGenieSwitcher';
import { PrintOrderCard } from './components/PrintOrderCard';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';

const ActivationPackDetailPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { packId } = useParams<{ packId: string }>();
  const { data: pack, isLoading } = useActivationPack(packId || '');
  const { mutate: updatePack, isPending: isUpdating } = useUpdateActivationPack();
  const { mutate: deletePack, isPending: isDeleting } = useDeleteActivationPack();
  const { downloadAllAssets, isGenerating } = useAssetGenerator(pack || null);
  const { data: allPacks = [] } = useActivationPacks();

  // Remembered so the Genie switcher and deep links can return here. The
  // Activation front door no longer follows it — it opens on the Genie list,
  // the same way Knowledge Base and My Genies do.
  useRememberActivationPack(pack?.id);

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [channels, setChannels] = useState<ChannelConfig>({ ...DEFAULT_CHANNEL_CONFIG });
  const [touchpoints, setTouchpoints] = useState<TouchpointConfig>({ ...DEFAULT_TOUCHPOINT_CONFIG });
  const [assetConfig, setAssetConfig] = useState<AssetConfig>({ ...DEFAULT_ASSET_CONFIG });

  // Edit mode states
  const [isEditingOverview, setIsEditingOverview] = useState(false);
  const [isEditingChannels, setIsEditingChannels] = useState(false);
  const [isEditingMaterials, setIsEditingMaterials] = useState(false);
  const [isEditingCustomization, setIsEditingCustomization] = useState(false);

  // Delete confirmation
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  // Copy state
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [copiedAppLink, setCopiedAppLink] = useState(false);
  const [isDownloadingCompletePack, setIsDownloadingCompletePack] = useState(false);

  const [pendingScrollId, setPendingScrollId] = useState<string | null>(null);
  const handledHashRef = useRef<string | null>(null);

  // The kit behind the three booklets — the saved copy layered over the
  // capability starter kit — and the same kit with nothing saved, which is what
  // "back to the starter kit" restores and what the editor shows as placeholder
  // text. Both are null until this Genie has a published page, which is the
  // same condition that hides the booklets themselves.
  const printKit = useMemo(() => (pack ? resolvePackPrintKit(pack) : null), [pack]);
  const printPresetKit = useMemo(() => (pack ? resolvePackPresetKit(pack) : null), [pack]);

  // Brand color
  const brand = pack?.agent?.branding?.primaryColor || pack?.portal?.branding?.primaryColor || '#4E9CFF';
  const targetBranding = pack?.agent?.branding || pack?.portal?.branding || null;
  const avatarUrl =
    targetBranding?.logoUrl || targetBranding?.customAvatarUrl || undefined;
  const hasTargetBranding = Boolean(
    targetBranding &&
      (targetBranding.primaryColor !== '#4E9CFF' ||
        targetBranding.secondaryColor ||
        targetBranding.customAvatarUrl ||
        targetBranding.logoUrl ||
        targetBranding.gradientEnabled),
  );

  // Initialize form when pack loads
  useEffect(() => {
    if (pack) {
      setName(pack.name);
      setDescription(pack.description || '');
      setChannels(pack.channels);
      setTouchpoints(pack.touchpoints);
      setAssetConfig(pack.asset_config);
    }
  }, [pack]);

  const handleSave = () => {
    if (!packId) return;

    updatePack(
      {
        packId,
        updates: {
          name,
          description: description || undefined,
          channels,
          touchpoints,
          asset_config: assetConfig,
        },
      },
      {
        onSuccess: () => {
          setIsEditingOverview(false);
          setIsEditingChannels(false);
          setIsEditingMaterials(false);
          setIsEditingCustomization(false);
        },
      }
    );
  };

  const handleDelete = () => {
    if (!packId) return;
    deletePack(packId, {
      onSuccess: () => {
        navigate('/genie-hub/activation/all');
      },
    });
  };

  const toggleChannel = (key: ChannelType) => {
    setChannels((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleTouchpoint = (key: TouchpointType) => {
    setTouchpoints((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleCopyUrl = async () => {
    if (!pack) return;
    try {
      const url = ActivationPackService.getTargetUrl(pack);
      await navigator.clipboard.writeText(url);
      ActivationFunnelService.packShared(
        ActivationFunnelService.packGenieId(pack),
        'link',
        { link: 'target' },
      );
      setCopiedUrl(true);
      toast.success('Link copied to clipboard');
      setTimeout(() => setCopiedUrl(false), 2000);
    } catch {
      toast.error('Failed to copy link');
    }
  };

  const handleCopyAppLink = async () => {
    if (!pack) return;
    try {
      const url = ActivationPackService.getAppSmartLinkUrl(pack);
      await navigator.clipboard.writeText(url);
      ActivationFunnelService.packShared(
        ActivationFunnelService.packGenieId(pack),
        'link',
        { link: 'app_smart_link' },
      );
      setCopiedAppLink(true);
      toast.success('Install link copied to clipboard');
      setTimeout(() => setCopiedAppLink(false), 2000);
    } catch {
      toast.error('Failed to copy app link');
    }
  };

  const handleDownloadCompletePack = async () => {
    if (!pack || isDownloadingCompletePack || isGenerating) return;
    setIsDownloadingCompletePack(true);
    try {
      await downloadAllAssets();
      toast.success('Downloaded your complete activation pack');
    } catch (downloadError) {
      console.error('Failed to build complete activation pack:', downloadError);
      toast.error("Couldn't build a complete activation pack", {
        description:
          downloadError instanceof Error
            ? downloadError.message
            : 'Please try again. No partial bundle was downloaded.',
      });
    } finally {
      setIsDownloadingCompletePack(false);
    }
  };

  /** Jump to a section by id. Every section is mounted, so this is just a scroll. */
  const jumpToSection = useCallback((id: string) => {
    setPendingScrollId(id);
  }, []);

  // Retry the scroll for a few frames: the "Pack settings" block is still a
  // collapsible, so its anchors need a render before they exist in the document.
  useEffect(() => {
    if (!pendingScrollId) return;
    let attempts = 0;
    let timer: ReturnType<typeof setTimeout>;
    const attempt = () => {
      const element = document.getElementById(pendingScrollId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setPendingScrollId(null);
        return;
      }
      attempts += 1;
      if (attempts > 20) {
        setPendingScrollId(null);
        return;
      }
      timer = setTimeout(attempt, 60);
    };
    timer = setTimeout(attempt, 80);
    return () => clearTimeout(timer);
  }, [pendingScrollId]);

  // Deep links (e.g. from the old Genie Access page, or a share/QR modal)
  // land with a #activate-* hash — open the right container, then jump there
  // once the pack has rendered. Handled once per pack + hash so a background
  // refetch never re-hijacks the user's scroll position.
  useEffect(() => {
    if (!pack || !location.hash) return;
    const key = `${pack.id}${location.hash}`;
    if (handledHashRef.current === key) return;
    handledHashRef.current = key;
    jumpToSection(location.hash.slice(1));
  }, [pack, location.hash, jumpToSection]);

  const hasUnsavedChanges =
    isEditingOverview || isEditingChannels || isEditingMaterials || isEditingCustomization;

  if (isLoading) {
    return <GenieLoader size="fullscreen" loadingText="Loading activation pack…" />;
  }

  if (!pack) {
    return (
      <div className="min-h-screen bg-[#F9FAFC] flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Pack Not Found</h2>
          <p className="text-gray-600 mb-4">
            The activation pack you're looking for doesn't exist or you don't have access to it.
          </p>
          <Button onClick={() => navigate('/genie-hub/activation/all')}>Back to Activation</Button>
        </div>
      </div>
    );
  }

  const targetType = ActivationPackService.getTargetType(pack);
  const targetName = ActivationPackService.getTargetName(pack);

  // Stay inside the Genie loop: if we know which Genie this pack belongs to,
  // "Back" returns to that Genie's workspace rather than the global activation
  // list. An explicit origin in router state (e.g. opened from the global list)
  // takes precedence so that browsing flow keeps its own back behavior.
  const backTarget: ActivationBackOrigin =
    (location.state as { from?: ActivationBackOrigin } | null)?.from ??
    (pack.agent_id
      ? { path: `/genie-hub/my-genies/${pack.agent_id}`, label: targetName }
      : { path: '/genie-hub/activation/all', label: 'Activation' });

  return (
    <div className="min-h-screen bg-[#F9FAFC]">
      <div className="container mx-auto px-4 py-6 md:py-8 max-w-[1400px]">
        {/* Back Button — stays within the Genie loop when we know the origin */}
        <button
          onClick={() => navigate(backTarget.path)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="font-medium">Back to {backTarget.label}</span>
        </button>

        {/* Header Section */}
        <div className="p-[2px] rounded-2xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] mb-6">
          <div className="rounded-[14px] bg-white overflow-hidden">
            {/* Gradient Header Bar */}
            <div className="space-y-2 lg:relative p-2 lg:p-0 flex flex-col lg:flex-row items-center justify-between">
              <div
                className="lg:h-16 w-full"
                style={{
                  background: `linear-gradient(135deg, ${brand}15, #FF4DB815)`,
                }}
              />

              {/* Badges on gradient */}
              <div className="lg:absolute top-4 left-6 flex items-center flex-wrap gap-2">
                <span
                  className="px-3 py-1.5 rounded-full text-xs font-medium shadow-sm border bg-white"
                  style={{ color: brand, borderColor: `${brand}55` }}
                >
                  Activation Pack
                </span>
                <div
                  className="px-3 py-1.5 rounded-full text-xs flex items-center shadow-sm border bg-white"
                  style={{ borderColor: `${brand}55`, color: brand }}
                >
                  {targetType !== 'genie' && (
                    <Globe className="w-3 h-3 mr-1" />
                  )}
                  <span className="font-medium">{targetName}</span>
                </div>
                <PackGenieSwitcher packs={allPacks} currentPackId={pack.id} brand={brand} />
              </div>
            </div>

            {/* Main Header Content */}
            <div className="px-6 md:px-8 py-6">
              <div className="flex flex-col md:flex-row items-center md:items-start gap-4 md:gap-8">
                <div className="flex-shrink-0 transform scale-[0.45] md:scale-[0.55] -my-16 md:-my-20 md:-mx-12">
                  <GenieAvatar
                    color={brand}
                    secondaryColor={targetBranding?.secondaryColor}
                    gradientEnabled={targetBranding?.gradientEnabled}
                    gradientAngle={targetBranding?.gradientAngle}
                    avatarBackgroundOpacity={targetBranding?.avatarBackgroundOpacity}
                    speaking={false}
                    listening={false}
                    isConnecting={false}
                    customAvatarUrl={avatarUrl}
                    hasCustomBranding={hasTargetBranding}
                  />
                </div>
                <div className="flex-1 min-w-0 text-center md:text-left">
                  <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">{pack.name}</h1>
                  {pack.description && (
                    <p className="text-base text-gray-600 mb-4">{pack.description}</p>
                  )}

                  {/* Exactly two actions above the fold. Everything else lives
                      inside "Customise your pack" below. */}
                  <div className="flex flex-wrap items-center justify-center md:justify-start gap-2">
                    <button
                      onClick={handleDownloadCompletePack}
                      disabled={isDownloadingCompletePack || isGenerating}
                      className="flex w-full sm:w-auto items-center justify-center gap-2 px-5 py-2.5 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all hover:scale-105 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:scale-100"
                      style={{
                        background: `linear-gradient(135deg, ${brand}, #FF4DB8)`,
                      }}
                    >
                      {isDownloadingCompletePack || isGenerating ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Download className="w-4 h-4" />
                      )}
                      {isDownloadingCompletePack || isGenerating
                        ? 'Building everything…'
                        : 'Download everything'}
                    </button>
                    <button
                      onClick={() => jumpToSection('print-order')}
                      className="flex w-full sm:w-auto items-center justify-center gap-2 px-5 py-2.5 bg-white border-2 rounded-xl font-semibold hover:bg-gray-50 transition-all hover:scale-105"
                      style={{ borderColor: brand, color: brand }}
                    >
                      <Printer className="w-4 h-4" />
                      Send it to my printer
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* The three booklets. Same set for every Genie, built on open, one
            click from the printer. This is what the page is for. */}
        <PackBooklets pack={pack} brand={brand} />

        {/* The words on those sheets. Directly under the covers on purpose:
            this is where somebody looking at generic starter copy fixes it. */}
        {printKit && printPresetKit && (
          <div className="mb-4 lg:mb-6">
            <PackPrintCopySection
              pack={pack}
              kit={printKit}
              preset={printPresetKit}
              brand={brand}
            />
          </div>
        )}

        {/* Main Content — the print handoff up top, everything else tucked
            inside one collapsed "Customise your pack" block. */}
        <div className="space-y-4 lg:space-y-6">
          {/* Production handoff to the customer's preferred printer. */}
          <div id="print-order" className="scroll-mt-6">
            <PrintOrderCard pack={pack} brand={brand} />
          </div>

          {/* Always open. It was a collapsible and it hid the half of the page
              people came for behind a chevron nobody pressed. */}
          <div className="p-[2px] rounded-2xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]">
            <div className="rounded-[14px] bg-white overflow-hidden">
              <div className="flex items-center gap-3 px-4 md:px-6 py-4 md:py-5">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${brand}15` }}>
                  <Settings2 className="w-5 h-5" style={{ color: brand }} />
                </div>
                <div className="min-w-0">
                  <h3 className="text-lg font-bold text-gray-900">Customise your pack</h3>
                  <p className="text-sm text-gray-600">
                    Posters, lookbook, QR codes, app onboarding, email signature, launch copy, access &amp; settings
                  </p>
                </div>
              </div>

              <div>
                <div className="px-3 md:px-4 pb-4 pt-1 space-y-4 lg:space-y-6">
                  {/* Share links — kept out of the two-action header, still one click away. */}
                  <div className="flex flex-wrap items-center gap-2 px-1 pt-2">
                    <button
                      onClick={handleCopyUrl}
                      className="flex items-center gap-2 px-4 py-2 bg-white border-2 rounded-xl text-sm font-semibold hover:bg-gray-50 transition-all"
                      style={{ borderColor: brand, color: brand }}
                    >
                      {copiedUrl ? <Check className="w-4 h-4" /> : <Link2 className="w-4 h-4" />}
                      {copiedUrl ? 'Copied!' : 'Share link'}
                    </button>
                    <button
                      onClick={handleCopyAppLink}
                      className="flex items-center gap-2 px-4 py-2 bg-white border-2 rounded-xl text-sm font-semibold hover:bg-gray-50 transition-all"
                      style={{ borderColor: brand, color: brand }}
                    >
                      {copiedAppLink ? <Check className="w-4 h-4" /> : <Smartphone className="w-4 h-4" />}
                      {copiedAppLink ? 'Copied!' : 'Share install link'}
                    </button>
                    <button
                      onClick={() => jumpToSection('activate-app')}
                      className="flex items-center gap-2 px-4 py-2 bg-white border-2 rounded-xl text-sm font-semibold hover:bg-gray-50 transition-all"
                      style={{ borderColor: brand, color: brand }}
                    >
                      <QrCode className="w-4 h-4" />
                      Share QR code
                    </button>
                    {hasUnsavedChanges && (
                      <button
                        onClick={handleSave}
                        disabled={isUpdating}
                        className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white transition-all disabled:opacity-50"
                        style={{ background: `linear-gradient(135deg, ${brand}, #FF4DB8)` }}
                      >
                        {isUpdating ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Check className="w-4 h-4" />
                        )}
                        Save changes
                      </button>
                    )}
                  </div>

                  {/* The one-click front door — mass-activate customers, not the account team */}
                  <ActivateCustomersCard pack={pack} brand={brand} />

                  {/* Hero: the power moves, for anyone who wants to go step by step */}
                  <PackActivateHero brand={brand} onJump={jumpToSection} showInvite={!!pack.agent_id} />

                  {/* The complete handoff — the fastest route from setup to launch day */}
                  <div id="activation-assets" className="scroll-mt-6">
                    <PackAssetsSection pack={pack} brand={brand} />
                  </div>

                  {/* Mobile adoption. */}
                  <div id="activate-app" className="space-y-4 lg:space-y-6 scroll-mt-6">
                    <PackAppOnboardingSection pack={pack} brand={brand} />
                    <PackActivateInAppSection pack={pack} brand={brand} />
                  </div>

                  <div id="activate-presentation" className="scroll-mt-6">
                    <PackPresentationSection pack={pack} brand={brand} />
                  </div>

                  <div id="activate-signature" className="scroll-mt-6">
                    <PackEmailSignatureSection pack={pack} brand={brand} />
                  </div>

                  {/* Placement inspiration and standalone poster previews. */}
                  <div id="activate-lookbook" className="scroll-mt-6">
                    <PackLookbookSection pack={pack} brand={brand} />
                  </div>
                  <div id="activate-posters" className="scroll-mt-6">
                    <PackPostersSection pack={pack} brand={brand} />
                  </div>

                  {/* Invite people directly (requests, access & who's already in). */}
                  {pack.agent_id && (
                    <div id="activate-invite" className="scroll-mt-6">
                      <PackInviteSection agentId={pack.agent_id} brand={brand} />
                    </div>
                  )}

                  {/* Promote — ready-to-paste launch copy */}
                  <div id="activate-promote" className="scroll-mt-6">
                    <PackPromoteSection pack={pack} brand={brand} />
                  </div>

                  {/* Pack settings — config tucked out of the way, still fully editable */}
                  <Collapsible id="pack-settings" className="p-[2px] rounded-xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] scroll-mt-6">
                    <div className="rounded-[10px] bg-white overflow-hidden">
                      <CollapsibleTrigger className="group w-full flex items-center justify-between gap-3 px-4 md:px-6 py-4 md:py-5 hover:bg-gray-50 transition-colors">
                        <div className="flex items-center gap-3 min-w-0 text-left">
                          <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${brand}15` }}>
                            <Settings2 className="w-5 h-5" style={{ color: brand }} />
                          </div>
                          <div className="min-w-0">
                            <h3 className="text-lg font-bold text-gray-900">Pack settings</h3>
                            <p className="text-sm text-gray-600">Name, channels, materials &amp; customization</p>
                          </div>
                        </div>
                        <ChevronDown className="w-5 h-5 text-gray-400 shrink-0 transition-transform group-data-[state=open]:rotate-180" />
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <div className="px-3 md:px-4 pb-4 pt-1 space-y-4">
                          <PackOverviewSection
                            pack={pack}
                            isEditing={isEditingOverview}
                            onEditToggle={() => setIsEditingOverview(!isEditingOverview)}
                            name={name}
                            description={description}
                            targetType={targetType as 'genie' | 'portal'}
                            targetId={pack.agent_id || pack.portal_id || ''}
                            onNameChange={setName}
                            onDescriptionChange={setDescription}
                            onTargetTypeChange={() => {}}
                            onTargetIdChange={() => {}}
                            agents={[]}
                            portals={[]}
                            brand={brand}
                          />
                          <PackChannelsSection
                            channels={channels}
                            isEditing={isEditingChannels}
                            onEditToggle={() => setIsEditingChannels(!isEditingChannels)}
                            onChannelToggle={toggleChannel}
                            brand={brand}
                          />
                          <PackMaterialsSection
                            touchpoints={touchpoints}
                            isEditing={isEditingMaterials}
                            onEditToggle={() => setIsEditingMaterials(!isEditingMaterials)}
                            onTouchpointToggle={toggleTouchpoint}
                            brand={brand}
                          />
                          <PackCustomizationSection
                            assetConfig={assetConfig}
                            touchpoints={touchpoints}
                            isEditing={isEditingCustomization}
                            onEditToggle={() => setIsEditingCustomization(!isEditingCustomization)}
                            onAssetConfigChange={(updates) =>
                              setAssetConfig((prev) => ({ ...prev, ...updates }))
                            }
                            brand={brand}
                          />
                          <PackQuickLinksSection pack={pack} brand={brand} />

                          <div className="pt-2 border-t border-gray-100 flex items-center justify-between gap-3">
                            {hasUnsavedChanges ? (
                              <Button
                                onClick={handleSave}
                                disabled={isUpdating}
                                className="text-white border-0"
                                style={{ background: `linear-gradient(135deg, ${brand}, #FF4DB8)` }}
                              >
                                {isUpdating ? (
                                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                ) : (
                                  <Check className="w-4 h-4 mr-2" />
                                )}
                                Save changes
                              </Button>
                            ) : (
                              <span />
                            )}
                            <button
                              onClick={() => setShowDeleteDialog(true)}
                              className="flex items-center gap-1.5 px-3 py-2 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                              Delete this pack
                            </button>
                          </div>
                        </div>
                      </CollapsibleContent>
                    </div>
                  </Collapsible>
                </div>
              </div>
            </div>
          </div>

          {/* This is useful after the pack is in circulation, not while someone
              is choosing the pieces to print and place. */}
          <PackActivationTracker
            pack={pack}
            brand={brand}
            onGoToPrintOrder={() => jumpToSection('print-order')}
          />
        </div>
      </div>

      <BaseSheet
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        title="Delete activation pack"
        description="This permanently removes the pack and its launch configuration."
        size="sm"
        loading={isDeleting}
        preventOutsideClick={isDeleting}
        footer={(
          <div className="flex w-full justify-start gap-2">
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)} disabled={isDeleting}>
              Cancel
            </Button>
            <Button onClick={handleDelete} disabled={isDeleting} className="bg-red-600 text-white hover:bg-red-700">
              <Trash2 className="mr-2 h-4 w-4" />
              Delete pack
            </Button>
          </div>
        )}
      >
        <p className="text-sm leading-6 text-gray-700">
          Delete <strong>{pack.name}</strong>? This action cannot be undone.
        </p>
      </BaseSheet>
    </div>
  );
};

export default ActivationPackDetailPage;
